<template>
  <v-card>
    <v-row class="mx-0">
      <v-col cols="2">
        <select-project
          v-model="project"
          show-select-all
          multiple
          :loading="isLoading"
        />
        <v-select
          v-model="searchBy"
          class="mt-2"
          label="Search by"
          :items="['login', 'account_id']"
          hide-details
        />
        <v-text-field
          v-model.number="loyalty_points"
          type="number"
          min="0"
          label="Set loyalty points to"
          class="mb-3"
          hide-details
        />
        <v-label class="pt-2 font-size-14">
          Set Balance
        </v-label>
        <v-row class="mx-0 flex-nowrap">
          <v-text-field
            v-model.number="balance_default"
            class="mt-0"
            prefix="Default"
            type="number"
            min="0"
            hint="Will be set for ALL currencies that are NOT set explicitly below"
            persistent-hint
            dense
          />
          <v-tooltip top>
            <template #activator="{ on }">
              <v-btn
                color="primary"
                icon
                v-on="on"
                @click="setDefaultBalances"
              >
                <v-icon>
                  mdi-auto-mode
                </v-icon>
              </v-btn>
            </template>
            Set/restore defaults
          </v-tooltip>
          <v-tooltip top>
            <template #activator="{on}">
              <v-btn
                icon
                color="error"
                v-on="on"
                :disabled="!balances.length"
                @click="balances = []"
              >
                <v-icon>
                  close
                </v-icon>
              </v-btn>
            </template>
            Clear currency config
          </v-tooltip>
        </v-row>
        <v-row
          v-for="(value, index) of balances"
          class="mx-0 flex-nowrap align-end balance-list"
          style="gap: 1rem;"
          :key="`funds-account-currencymap-${value.currency}-${index}`"
        >
          <v-select
            v-model="value.currency"
            class="currency-selector"
            :items="allCurrencyOptionsVSelectFormatted"
            placeholder="Currency"
            dense
            :item-disabled="item => usedCurrencies.includes(item.value)"
            hide-details
          />
          <v-text-field
            v-model.number="value.balance"
            type="number"
            min="0"
            placeholder="Balance"
            dense
            hide-details
          />
          <v-btn
            icon
            color="error"
            @click="balances.splice(index, 1)"
          >
            <v-icon>
              close
            </v-icon>
          </v-btn>
        </v-row>
        <v-row class="mx-0 justify-end">
          <v-btn
            color="primary"
            icon
            @click="balances.push({ currency: '', balance: null })"
          >
            <v-icon>
              add
            </v-icon>
          </v-btn>
        </v-row>
        <v-row class="mx-0 justify-end">
          <v-btn
            v-if="accounts.length"
            icon
            color="error"
            :loading="isLoading"
            @click="accounts = []"
          >
            <v-icon>
              close
            </v-icon>
          </v-btn>

          <v-btn
            icon
            color="primary"
            :disabled="!accounts.length"
            :loading="isLoading"
            @click="onUpdateConfirm"
          >
            <v-icon>
              check
            </v-icon>
          </v-btn>
        </v-row>
      </v-col>
      <v-col cols="10">
        <template v-if="!accounts.length">
          <v-textarea
            v-model="rawAccounts"
            label="Accounts"
            class="accounts-list-textarea"
            auto-grow
            :hint="`One ${searchBy} per line, click confirm when data is correct`"
          />
          <v-btn
            class="mt-2 ml-auto"
            color="primary"
            @click="onRawDataConfirm"
          >
            Confirm
          </v-btn>
        </template>

        <v-data-iterator
          v-else
          dense
          :headers="headers"
          :items="accounts"
          :footer-props="{ 'items-per-page-options': [50, 100, -1] }"
          :items-per-page="50"
        >
          <template v-slot:header>
            <h3>Search Term</h3>
          </template>


          <template v-slot:default="{items}">
            <v-row  style="max-height: calc(100vh - 160px); overflow-y: auto;">
              <v-col
                v-for="item in items"
                :key="item.search_term"
                cols="12"
                sm="6"
                md="3"
                lg="2"
              >
                <v-text-field
                  v-model="item.search_term"
                  style="max-width: 15rem;"
                  hide-details
                  dense
                  readonly
                />
              </v-col>
            </v-row>
          </template>
        </v-data-iterator>
      </v-col>
    </v-row>

    <v-dialog
      v-model="resultDialog"
      max-width="1200"
    >
      <v-data-table
        :items="fundedAccounts"
        :headers="resultHeaders"
      >
        <template #item.entity_id="{value}">
          {{ projectNameById[value] }}
        </template>
      </v-data-table>
    </v-dialog>
  </v-card>
</template>

<style lang="scss" scoped>

  .accounts-list-textarea {
    ::v-deep(textarea) {
      max-height: calc(100vh - 155px) !important;
      overflow-y: auto !important;
    }
  }

  .paymethods-autocomplete {
    ::v-deep(.v-input__control) {
      max-height: calc(100vh - 250px);
      overflow-y: auto;
      overflow-x: hidden;
    }
  }

  .balance-list .currency-selector ::v-deep .v-select__selection {
    color: inherit !important;
  }
</style>

<script>
import { uniq } from 'lodash'
import SelectProject from '@/components/core/SelectProject/SelectProject.vue'

// Most used currencies in testing
const DEFAULT_BALANCES = {
  EUR: 100,
  PLN: 250,
  CAD: 100,
  BRL: 300,
  COP: 150000,
  ARS: 8000,
  RUB: 9000,
  CLP: 30000,
  AUD: 100,
  NZD: 100,
  HUF: 10000,
  SEK: 100,
  TRY: 5100,
  JPY: 18400,
  USD: 120,
  NOK: 1200,
  CHF: 95,
  GBR: 90,
}

export default {
  components: {
    SelectProject,
  },
  props: {
    isLoading: Boolean,
    currencies: {
      type: Array,
      required: true,
    },
    projectNameById: {
      type: Object,
      required: true,
    },
    fundedAccounts: {
      type: Array,
      required: true,
    }
  },
  data() {
    return {
      project: [],
      rawAccounts: '',
      accounts: [],
      headers: [
        { text: 'Search Term', value: 'search_term' },
      ],
      resultHeaders: [
        { text: 'Project', value: 'entity_id' },
        { text: 'Account ID', value: 'account_id' },
        { text: 'Login', value: 'login' },
        { text: 'Currency', value: 'currency' },
        { text: 'Balance', value: 'balance' },
        { text: 'Loyalty Points', value: 'loyalty_points' },
      ],
      searchBy: 'login',
      loyalty_points: 10000,
      balance_default: null,
      balances: [],
      resultDialog: false,
    }
  },

  computed: {
    allCurrencyOptions() {
      return uniq([...this.currencies, ...Object.keys(DEFAULT_BALANCES)])
    },

    // VSelect item-disabled will not be called if the 'items' array consists of primitives
    allCurrencyOptionsVSelectFormatted() {
      return this.allCurrencyOptions.map(currency => ({text: currency, value: currency}))
    },

    usedCurrencies() {
      return this.balances.map(({currency}) => currency).filter(Boolean)
    },

    availableCurrencies() {
      return this.allCurrencyOptions.filter(currency => !this.usedCurrencies.includes(currency))
    },
  },

  methods: {
    onRawDataConfirm() {
      this.accounts = this.rawAccounts.split('\n').filter(entry => !!entry.trim()).map(search_term => ({ search_term }))
    },
    onUpdateConfirm() {
      this.$root.$confirm('Confirm', 'Are you sure you want to fund the accounts in the table?')
        .then(yes => {
          if (!yes) {
            return
          }

          const balance_map = {}
          for (const { currency, balance } of this.balances) {
            if (currency && Number.isFinite(balance)) {
              balance_map[currency] = balance
            }
          }

          this.$emit('fund', {
            accounts: this.accounts.map(({search_term}) => search_term),
            search_by: this.searchBy,
            by_entities: this.project,
            balance_map: balance_map,
            balance_default: this.balance_default,
            loyalty_points: this.loyalty_points,
          })
        })
    },

    setDefaultBalances() {
      this.balances = Object.keys(DEFAULT_BALANCES).map(currency => ({currency, balance: DEFAULT_BALANCES[currency]}))
    },
  },

  watch: {
    fundedAccounts(newList) {
      if (!!newList.length) {
        this.resultDialog = true
      }
    },
  }
}
</script>
